import type { TabsProps } from "antd";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import AuthSignUpCreatorTabComponent from "../../../components/AuthSignUpCreatorTabComponent";
import AuthSignUpFanTabComponent from "../../../components/AuthSignUpFanTabComponent";
import AuthTabsCompnent from "../../../components/AuthTabsComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import { CREATOR } from "../../../helpers/constant";
import { IUser } from "../../../helpers/types";
import AuthGoogleUnavailableComponent from "../../../components/AuthGoogleUnavailableComponent";
import useSignUpPageInfos from "../../../hooks/global/useSignUpPageInfos";
import { authService } from "../../../services";

const stepMap: { [key: string]: number } = {
  zero: -1,
  first: 2,
  second: 3,
  third: 4,
  fourth: 5,
};

const SignUpPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const [authGoogleUnavailable, setAuthGoogleUnavailable] = useState<boolean>(false);
  const [curentStep, setCurrentStep] = useState<number>(0);
  const [usersId, setUsersId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const id = searchParams.get("id");
  const email = searchParams.get("email");
  const tabValue: any = searchParams.get("currentTab");

  // Détecter si on est sur l'onglet créateur
  const hasCreatorToken = authService.isAuthenticated() && authService.getDecodedToken()?.source === "creator";
  const isCreatorTab = !!(tabValue === CREATOR || token || hasCreatorToken);

  useSignUpPageInfos(t, isCreatorTab);

  const googleUnavailable = () => {
    setAuthGoogleUnavailable(true);
  };

  const googleUnavailableCancel = () => {
    setAuthGoogleUnavailable(false);
  };

  useEffect(() => {
    const token = authService.getToken("token");
    const hasToken = !!token;
    const decodedToken = hasToken ? authService.getDecodedToken() : null;

    if (hasToken || (id && email)) {
      if (hasToken && decodedToken) {
        const step = authService.getCreatorRegistrationStep();

        if (step && stepMap[step] !== undefined) {
          const mappedStep = stepMap[step];
          setCurrentStep(mappedStep);
        } else {
          setCurrentStep(2);
        }

        setUsersId(decodedToken.sourceId);
        setUserEmail(decodedToken.email || null);

        const currentUrl = window.location.href;
        if (!currentUrl.includes("currentTab=creator")) {
          navigate(`/auth/signUp?currentTab=creator`, { replace: true });
        }

        if (step === "zero") {
          setCurrentUser({
            _id: decodedToken.sourceId,
            email: decodedToken.email,
            firstName: decodedToken.firstName,
            lastName: decodedToken.lastName,
            name: (decodedToken as any).name || "",
            username: "",
          } as IUser);
        }
      } else if (id && email) {
        setUserEmail(email);
        setUsersId(id);
        setCurrentStep(1);
      }

      setIsInitialized(true);
    } else {
      setCurrentStep(1);
      setIsInitialized(true);
    }
  }, []);

  const tabs: TabsProps["items"] = [
    {
      key: "creator",
      label: t("auth-tab-creator"),
      children: (
        <AuthSignUpCreatorTabComponent
          setCurrentStep={setCurrentStep}
          curentStep={curentStep}
          setUserEmail={setUserEmail}
          userEmail={userEmail}
          setUsersId={setUsersId}
          usersId={usersId}
          setCurrentUser={setCurrentUser}
          currentUser={currentUser}
          googleUnavailable={googleUnavailable}
        />
      ),
    },
    {
      key: "fan",
      label: t("auth-tab-fan"),
      children: <AuthSignUpFanTabComponent googleUnavailable={googleUnavailable} />,
    },
  ];

  // Determine default tab based on token - always creator if has creator token
  const getDefaultTab = () => {
    if (hasCreatorToken) {
      return "creator";
    }
    return tabValue === CREATOR || token ? "creator" : "fan";
  };

  return (
    <>
      {authGoogleUnavailable ? (
        <AuthGoogleUnavailableComponent cancel={googleUnavailableCancel} />
      ) : !isInitialized ? (
        <div className="gradient-background h-full-screen d-flex justify-content-center align-items-center">
          <SpinnerComponent />
        </div>
      ) : (
        <div className="gradient-background">
          <h1 className="seo-hidden">{t("signup-page-h1")}</h1>
          {(() => {
            const isAuthenticated = authService.isAuthenticated();
            const showTabs = curentStep === 0 || curentStep === 1 || (curentStep === -1 && !isAuthenticated);

            return showTabs ? (
              <div className="h-full">
                <AuthTabsCompnent tabs={tabs} defaultActiveKey={getDefaultTab()} />
              </div>
            ) : (
              <div className="h-full-screen">
                <AuthSignUpCreatorTabComponent
                  setCurrentStep={setCurrentStep}
                  curentStep={curentStep}
                  setUserEmail={setUserEmail}
                  userEmail={userEmail}
                  setUsersId={setUsersId}
                  usersId={usersId}
                  setCurrentUser={setCurrentUser}
                  currentUser={currentUser}
                  googleUnavailable={googleUnavailable}
                />
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
};

export default SignUpPage;
